import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import Layout from "../components/Layout";
import { fmtDateMed } from "../lib/dates";
import { Plus, Trash2, Check, X, CalendarOff, Settings, Loader2, Save, Pencil } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import { format } from "date-fns";
import { Avatar, AvatarFallback } from "../components/ui/avatar";

const TimeOff = () => {
  const [requests, setRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);  // request_id when editing, null when creating
  const [typesDialogOpen, setTypesDialogOpen] = useState(false);
  const [savingTypes, setSavingTypes] = useState(false);
  const [editTypes, setEditTypes] = useState([]);
  const [formData, setFormData] = useState({
    employee_id: "",
    start_date: null,
    end_date: null,
    reason: "",
    leave_type: "annual",
    work_days_counted: "",  // empty string = auto-calc weekdays in range
    auto_approve: true
  });

  // Inclusive day count for a date range
  const rangeTotalDays = (start, end) => {
    if (!start || !end) return 0;
    return Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
  };
  // Weekdays-only (Mon-Fri) count for a range — sensible default for "work days counted"
  const rangeWeekdays = (start, end) => {
    if (!start || !end) return 0;
    let count = 0;
    const d = new Date(start);
    while (d <= end) {
      const dow = d.getDay();
      if (dow !== 0 && dow !== 6) count += 1;
      d.setDate(d.getDate() + 1);
    }
    return count;
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reqRes, empRes, ltRes] = await Promise.all([
        axios.get(`${API}/time-off`, { withCredentials: true }),
        axios.get(`${API}/employees`, { withCredentials: true }),
        axios.get(`${API}/leave-types`, { withCredentials: true }),
      ]);
      setRequests(reqRes.data);
      setEmployees(empRes.data);
      setLeaveTypes(ltRes.data?.leave_types || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load time-off requests");
    } finally {
      setLoading(false);
    }
  };

  const openEditTypes = () => {
    setEditTypes(JSON.parse(JSON.stringify(leaveTypes)));
    setTypesDialogOpen(true);
  };

  const updateTypeField = (idx, field, value) => {
    setEditTypes((prev) => prev.map((t, i) => (i === idx ? { ...t, [field]: value } : t)));
  };

  const addNewType = () => {
    setEditTypes((prev) => [
      ...prev,
      { key: `custom_${Date.now()}`, name: "New leave type", color: "#94A3B8", counts_toward_work_days: false, max_credit_days_per_period: null, is_default: false },
    ]);
  };

  const removeType = (idx) => {
    setEditTypes((prev) => prev.filter((_, i) => i !== idx));
  };

  const saveLeaveTypes = async () => {
    if (!editTypes.length) {
      toast.error("Keep at least one leave type");
      return;
    }
    setSavingTypes(true);
    try {
      const payload = editTypes.map((t) => ({
        key: t.key,
        name: t.name,
        color: t.color,
        counts_toward_work_days: !!t.counts_toward_work_days,
        max_credit_days_per_period:
          t.max_credit_days_per_period === "" || t.max_credit_days_per_period === null || t.max_credit_days_per_period === undefined
            ? null
            : Number(t.max_credit_days_per_period),
        is_default: !!t.is_default,
      }));
      const res = await axios.put(`${API}/leave-types`, { leave_types: payload }, { withCredentials: true });
      setLeaveTypes(res.data?.leave_types || payload);
      setTypesDialogOpen(false);
      toast.success("Leave types saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingTypes(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.employee_id || !formData.start_date || !formData.end_date) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const payload = {
        employee_id: formData.employee_id,
        start_date: format(formData.start_date, "yyyy-MM-dd"),
        end_date: format(formData.end_date, "yyyy-MM-dd"),
        reason: formData.reason,
        leave_type: formData.leave_type || "annual",
      };
      // Status: in create-mode, auto_approve controls it; in edit-mode, send whatever the form has set
      if (editingId) {
        if (formData.status) payload.status = formData.status;
      } else if (formData.auto_approve) {
        payload.status = "approved";
      }
      // Optional override of how many days should count toward shifts total
      const wdc = String(formData.work_days_counted || "").trim();
      if (wdc !== "") {
        const n = parseInt(wdc, 10);
        if (Number.isFinite(n) && n >= 0) payload.work_days_counted = n;
      } else if (editingId) {
        // Explicitly clear it on edit if the user emptied the field
        payload.work_days_counted = null;
      }
      if (editingId) {
        await axios.put(`${API}/time-off/${editingId}`, payload, { withCredentials: true });
        toast.success("Time-off request updated");
      } else {
        await axios.post(`${API}/time-off`, payload, { withCredentials: true });
        toast.success(formData.auto_approve ? "Leave added and approved — roster will be blocked" : "Time-off request created");
      }
      setDialogOpen(false);
      setEditingId(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Failed to save request:", error);
      toast.error(editingId ? "Failed to update request" : "Failed to create request");
    }
  };

  const openEdit = (request) => {
    const parseDate = (s) => { const d = new Date(`${s}T00:00:00`); return Number.isNaN(d.getTime()) ? null : d; };
    setFormData({
      employee_id: request.employee_id,
      start_date: parseDate(request.start_date),
      end_date: parseDate(request.end_date),
      reason: request.reason || "",
      leave_type: request.leave_type || "annual",
      work_days_counted: request.work_days_counted == null ? "" : String(request.work_days_counted),
      status: request.status,
      auto_approve: false,
    });
    setEditingId(request.request_id);
    setDialogOpen(true);
  };

  const handleStatusChange = async (requestId, status) => {
    try {
      await axios.put(`${API}/time-off/${requestId}`, { status }, { withCredentials: true });
      toast.success(`Request ${status}`);
      fetchData();
    } catch (error) {
      console.error("Failed to update request:", error);
      toast.error("Failed to update request");
    }
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm("Are you sure you want to delete this request?")) return;
    
    try {
      await axios.delete(`${API}/time-off/${requestId}`, { withCredentials: true });
      toast.success("Request deleted");
      fetchData();
    } catch (error) {
      console.error("Failed to delete request:", error);
      toast.error("Failed to delete request");
    }
  };

  const resetForm = () => {
    setFormData({
      employee_id: "",
      start_date: null,
      end_date: null,
      reason: "",
      leave_type: "annual",
      work_days_counted: "",
      auto_approve: true
    });
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(e => e.employee_id === employeeId);
    return employee?.name || "Unknown";
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "approved":
        return "bg-emerald-50 text-emerald-700";
      case "rejected":
        return "bg-red-50 text-red-700";
      default:
        return "bg-amber-50 text-amber-700";
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl tracking-tight font-bold text-zinc-900 dark:text-zinc-100">Time Off Requests</h1>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
              Manage employee time-off requests
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={openEditTypes}
              data-testid="edit-leave-types-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              Edit leave types
            </Button>
            <Button
              onClick={() => {
                resetForm();
                setDialogOpen(true);
              }}
              className="bg-[#002FA7] hover:bg-[#00227A] text-white font-medium rounded"
              data-testid="add-timeoff-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Request
            </Button>
          </div>
        </div>

        {/* Requests List */}
        {requests.length === 0 ? (
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-12 text-center">
            <div className="w-12 h-12 bg-zinc-100 dark:bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <CalendarOff className="w-6 h-6 text-zinc-400 dark:text-zinc-500" />
            </div>
            <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1">No time-off requests</h3>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-4">Add time-off requests for your employees</p>
          </div>
        ) : (
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-800/50">
                  <th className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 text-left py-3 px-4">
                    Employee
                  </th>
                  <th className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 text-left py-3 px-4">
                    Dates
                  </th>
                  <th className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 text-left py-3 px-4 hidden md:table-cell">
                    Reason
                  </th>
                  <th className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 text-left py-3 px-4">
                    Status
                  </th>
                  <th className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 text-right py-3 px-4">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
                {requests.map((request) => (
                  <tr key={request.request_id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <Avatar className="w-8 h-8 border border-zinc-200 dark:border-zinc-800">
                          <AvatarFallback className="bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 text-xs font-medium">
                            {getInitials(getEmployeeName(request.employee_id))}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                            {getEmployeeName(request.employee_id)}
                          </span>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            {request.leave_type && (
                              <span className="text-xs bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 px-1.5 py-0.5 rounded">
                                {request.leave_type.replace(/_/g, " ")}
                              </span>
                            )}
                            {request.submitted_by === "staff" && (
                              <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">
                                Staff request
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm tabular-nums text-zinc-900 dark:text-zinc-100">
                        {fmtDateMed(request.start_date)}
                      </div>
                      <div className="text-xs text-zinc-500 dark:text-zinc-400">
                        to {fmtDateMed(request.end_date)}
                      </div>
                    </td>
                    <td className="py-3 px-4 hidden md:table-cell">
                      <span className="text-sm text-zinc-600 dark:text-zinc-400">
                        {request.reason || "—"}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`
                        text-xs font-medium px-2 py-1 rounded capitalize
                        ${getStatusBadge(request.status)}
                      `}>
                        {request.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {request.status === "pending" && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleStatusChange(request.request_id, "approved")}
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              data-testid={`approve-${request.request_id}`}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleStatusChange(request.request_id, "rejected")}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`reject-${request.request_id}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEdit(request)}
                          className="text-zinc-400 dark:text-zinc-500 hover:text-[#002FA7] dark:hover:text-blue-400"
                          data-testid={`edit-timeoff-${request.request_id}`}
                          title="Edit request"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(request.request_id)}
                          className="text-zinc-400 dark:text-zinc-500 hover:text-red-600"
                          data-testid={`delete-timeoff-${request.request_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Add Dialog */}
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditingId(null); }}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl tracking-tight font-bold">
                {editingId ? "Edit Time Off Request" : "Add Time Off Request"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 items-start">
              <div>
                <Label className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                  Employee *
                </Label>
                <Select
                  value={formData.employee_id}
                  onValueChange={(value) => setFormData({ ...formData, employee_id: value })}
                >
                  <SelectTrigger className="mt-1" data-testid="employee-select">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((employee) => (
                      <SelectItem key={employee.employee_id} value={employee.employee_id}>
                        {employee.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                    Start Date *
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full mt-1 justify-start text-left font-normal"
                        data-testid="start-date-btn"
                      >
                        {formData.start_date ? fmtDateMed(formData.start_date) : "Pick date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.start_date}
                        onSelect={(date) => setFormData({ ...formData, start_date: date })}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                    End Date *
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full mt-1 justify-start text-left font-normal"
                        data-testid="end-date-btn"
                      >
                        {formData.end_date ? fmtDateMed(formData.end_date) : "Pick date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.end_date}
                        onSelect={(date) => setFormData({ ...formData, end_date: date })}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Work days counted — controls how many of the leave days count toward shifts total */}
              {(() => {
                const total = rangeTotalDays(formData.start_date, formData.end_date);
                const suggestedDefault = rangeWeekdays(formData.start_date, formData.end_date);
                const raw = String(formData.work_days_counted || "").trim();
                const numericVal = raw === "" ? null : parseInt(raw, 10);
                const overLimit = total > 0 && numericVal !== null && Number.isFinite(numericVal) && numericVal > total;
                return (
                  <div data-testid="work-days-counted-field">
                    <Label htmlFor="work-days-counted" className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                      Work days counted <span className="text-zinc-400 normal-case">(numerals only)</span>
                    </Label>
                    <Input
                      id="work-days-counted"
                      type="number"
                      inputMode="numeric"
                      min={0}
                      step={1}
                      value={formData.work_days_counted}
                      placeholder={total > 0 ? `Auto: ${suggestedDefault} weekday${suggestedDefault === 1 ? "" : "s"} in range` : "Pick dates first"}
                      onChange={(e) => {
                        // Strip anything non-numeric defensively
                        const clean = e.target.value.replace(/[^0-9]/g, "");
                        setFormData({ ...formData, work_days_counted: clean });
                      }}
                      className={`mt-1 ${overLimit ? "border-red-500 text-red-600 focus-visible:ring-red-500" : ""}`}
                      data-testid="work-days-counted-input"
                    />
                    <p
                      className={`text-[11px] mt-1 ${overLimit ? "text-red-600 font-medium" : "text-zinc-500 dark:text-zinc-400"}`}
                      data-testid="work-days-counted-help"
                    >
                      {total === 0
                        ? "Pick the start and end dates first."
                        : overLimit
                        ? `Value ${numericVal} exceeds the total ${total} day${total === 1 ? "" : "s"} in the range. Please reduce it.`
                        : `Range is ${total} day${total === 1 ? "" : "s"} total. Leave blank to auto-count weekdays (${suggestedDefault}).`}
                    </p>
                  </div>
                );
              })()}

              <div>
                <Label htmlFor="reason" className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                  Reason (optional)
                </Label>
                <Textarea
                  id="reason"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  placeholder="e.g., Family vacation, medical appointment"
                  className="mt-1"
                  rows={2}
                  data-testid="reason-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                    Leave Type
                  </Label>
                  <Select
                    value={formData.leave_type}
                    onValueChange={(value) => setFormData({ ...formData, leave_type: value })}
                  >
                    <SelectTrigger className="mt-1" data-testid="leave-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(leaveTypes.length ? leaveTypes : [{ key: "annual", name: "Annual leave" }]).map((lt) => (
                        <SelectItem key={lt.key} value={lt.key} data-testid={`leave-type-option-${lt.key}`}>
                          {lt.name}{lt.counts_toward_work_days === false ? " (no work-day credit)" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                    {editingId ? "Status" : "Block roster?"}
                  </Label>
                  {editingId ? (
                    <Select
                      value={formData.status || "pending"}
                      onValueChange={(value) => setFormData({ ...formData, status: value })}
                    >
                      <SelectTrigger className="mt-1" data-testid="status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="approved">Approved (blocks roster)</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Select
                      value={formData.auto_approve ? "yes" : "no"}
                      onValueChange={(value) => setFormData({ ...formData, auto_approve: value === "yes" })}
                    >
                      <SelectTrigger className="mt-1" data-testid="auto-approve-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes, approve & block</SelectItem>
                        <SelectItem value="no">No, leave pending</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 -mt-2 sm:col-span-2">
                Approved leave is automatically blocked off — the roster engine will not assign this employee to shifts during these dates.
              </p>

              <div className="flex justify-end gap-3 pt-4 sm:col-span-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  data-testid="cancel-timeoff-btn"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-[#002FA7] hover:bg-[#00227A] text-white"
                  data-testid="save-timeoff-btn"
                >
                  {editingId ? "Save changes" : "Create Request"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Leave Types dialog */}
        <Dialog open={typesDialogOpen} onOpenChange={setTypesDialogOpen}>
          <DialogContent className="sm:max-w-3xl" data-testid="edit-types-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl tracking-tight font-bold">
                Leave types & roster impact
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 -mt-2">
              Configure how each leave type affects your roster engine. <span className="font-medium">Counts toward work days</span> means a leave day fulfils <span className="font-mono text-xs">min_shifts</span>/<span className="font-mono text-xs">min_hours</span> rules. Use the cap to limit how many days a single roster period gets credit for.
            </p>

            <div className="border border-zinc-200 dark:border-zinc-800 rounded mt-4 max-h-[480px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 dark:bg-zinc-800/60 text-[10px] uppercase tracking-[0.15em] text-zinc-500 dark:text-zinc-400">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Type</th>
                    <th className="text-left px-3 py-2 font-semibold">Counts toward work days</th>
                    <th className="text-left px-3 py-2 font-semibold">Max credit days / period</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
                  {editTypes.map((t, idx) => (
                    <tr key={t.key + idx} data-testid={`leave-type-row-${idx}`}>
                      <td className="px-3 py-2 align-top">
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={t.color || "#94A3B8"}
                            onChange={(e) => updateTypeField(idx, "color", e.target.value)}
                            className="w-7 h-7 rounded border border-zinc-200 dark:border-zinc-700 cursor-pointer"
                            data-testid={`leave-type-color-${idx}`}
                          />
                          <Input
                            value={t.name}
                            onChange={(e) => updateTypeField(idx, "name", e.target.value)}
                            className="text-sm"
                            data-testid={`leave-type-name-${idx}`}
                          />
                        </div>
                        <p className="text-[10px] text-zinc-400 dark:text-zinc-500 mt-1 font-mono">key: {t.key}</p>
                      </td>
                      <td className="px-3 py-2 align-top">
                        <Switch
                          checked={!!t.counts_toward_work_days}
                          onCheckedChange={(v) => updateTypeField(idx, "counts_toward_work_days", v)}
                          data-testid={`leave-type-counts-${idx}`}
                        />
                        <p className="text-[10px] text-zinc-500 dark:text-zinc-400 mt-1 max-w-[180px]">
                          {t.counts_toward_work_days
                            ? "Treated like a worked day for min_shifts / min_hours."
                            : "Blocks roster but staff still owe their full work-day count."}
                        </p>
                      </td>
                      <td className="px-3 py-2 align-top">
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          value={t.max_credit_days_per_period ?? ""}
                          onChange={(e) =>
                            updateTypeField(
                              idx,
                              "max_credit_days_per_period",
                              e.target.value === "" ? null : parseInt(e.target.value, 10)
                            )
                          }
                          placeholder="Unlimited"
                          disabled={!t.counts_toward_work_days}
                          className="w-28 text-sm tabular-nums"
                          data-testid={`leave-type-cap-${idx}`}
                        />
                        <p className="text-[10px] text-zinc-400 dark:text-zinc-500 mt-1 max-w-[160px]">
                          Cap credit days per roster (blank = no cap).
                        </p>
                      </td>
                      <td className="px-3 py-2 align-top text-right">
                        {t.is_default ? (
                          <span className="text-[10px] uppercase tracking-wide text-zinc-400 dark:text-zinc-500" title="Built-in type — name, colour and behaviour can be edited but it can't be deleted.">
                            built-in
                          </span>
                        ) : (
                          <button
                            onClick={() => removeType(idx)}
                            className="p-1.5 text-zinc-400 hover:text-red-500"
                            title="Remove this leave type"
                            data-testid={`leave-type-delete-${idx}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between gap-3 mt-4 flex-wrap">
              <Button
                variant="outline"
                onClick={addNewType}
                data-testid="leave-type-add-new"
              >
                <Plus className="w-4 h-4 mr-2" /> Add custom leave type
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setTypesDialogOpen(false)}
                  disabled={savingTypes}
                  data-testid="leave-types-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveLeaveTypes}
                  disabled={savingTypes}
                  className="bg-[#002FA7] hover:bg-[#00227A] text-white"
                  data-testid="leave-types-save"
                >
                  {savingTypes ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-2" /> Save changes</>}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default TimeOff;
